import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  List,
  Skeleton,
  Space,
  Statistic,
  Tag,
  Typography,
} from "antd";
import {
  ArrowRightOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  FolderOpenOutlined,
  InboxOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import { listProjects } from "services/pmService";
import { fetchWorkspaceMembershipsForUser } from "services/workspaceService";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { OLIVEEB_COLORS, workspaceStyles } from "workspaces/colors";
import { formatDateTime, readable } from "workspaces/workspaceUtils";

const { Text, Title } = Typography;

const glossyPanelStyle = {
  position: "relative",
  overflow: "hidden",
  border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
  borderRadius: 8,
  padding: 20,
  background: `linear-gradient(135deg, #ffffff 0%, ${OLIVEEB_COLORS.bgSoft} 46%, ${OLIVEEB_COLORS.oliveebBlueSoft} 100%)`,
  boxShadow: `0 18px 44px rgba(0, 64, 56, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.86)`,
  marginBottom: 16,
};

const shineStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 3,
  background: `linear-gradient(90deg, ${OLIVEEB_COLORS.oliveebGreen}, ${OLIVEEB_COLORS.oliveebBlue}, ${OLIVEEB_COLORS.oliveebOrange})`,
};

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);

const pageTotal = (data) =>
  typeof data?.totalElements === "number" ? data.totalElements : pageContent(data).length;

const userName = (user) => {
  const name = [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || user?.name || user?.username || user?.email || "there";
};

const workspaceName = (workspace) => workspace?.name || "Workspace";

const projectStatusColor = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "ACTIVE" || normalized === "OPEN") return "green";
  if (normalized === "COMPLETED" || normalized === "DONE") return "blue";
  if (normalized === "BLOCKED" || normalized === "PAUSED") return "gold";
  return "default";
};

const WelcomeHomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const {
    workspaces,
    selectedWorkspace,
    loading: workspacesLoading,
  } = useWorkspace();
  const [projectsPage, setProjectsPage] = useState({ content: [] });
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const defaultWorkspace = selectedWorkspace || workspaces[0] || null;
  const hasLinkedWorkspace = workspaces.length > 0;

  const loadWelcomeData = useCallback(async () => {
    if (!actorId || workspacesLoading) return;
    if (!hasLinkedWorkspace) {
      try {
        setLoading(true);
        setProjectsPage({ content: [] });
        setLoadError("");

        const membershipsResult = await fetchWorkspaceMembershipsForUser({
          actorId,
          userId: actorId,
          page: 0,
          size: 20,
          sort: "createdAt,desc",
        });

        setPendingInvitations(
          pageContent(membershipsResult).filter(
            (item) => String(item?.membershipStatus || "").toUpperCase() === "PENDING",
          ),
        );
      } catch (error) {
        setPendingInvitations([]);
        setLoadError(error?.message || "Workspace invitations could not be loaded.");
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      setLoadError("");

      const [projectsResult, membershipsResult] = await Promise.all([
        listProjects({ page: 0, size: 5, sort: "updatedAt,desc" }),
        fetchWorkspaceMembershipsForUser({
          actorId,
          userId: actorId,
          page: 0,
          size: 20,
          sort: "createdAt,desc",
        }),
      ]);

      setProjectsPage(projectsResult || { content: [] });
      setPendingInvitations(
        pageContent(membershipsResult).filter(
          (item) => String(item?.membershipStatus || "").toUpperCase() === "PENDING",
        ),
      );
    } catch (error) {
      setLoadError(error?.message || "Welcome data could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [actorId, hasLinkedWorkspace, workspacesLoading]);

  useEffect(() => {
    loadWelcomeData();
  }, [loadWelcomeData]);

  const projects = pageContent(projectsPage);
  const totalProjects = pageTotal(projectsPage);
  const totalTasksInView = useMemo(
    () => projects.reduce((sum, project) => sum + Number(project?.taskCount || 0), 0),
    [projects],
  );

  const stats = [
    {
      label: "Projects",
      value: loading ? "..." : totalProjects,
      color: OLIVEEB_COLORS.oliveebGreen,
      bg: OLIVEEB_COLORS.oliveebGreenSoft,
      to: paths.projects,
    },
    {
      label: "Tasks in recent projects",
      value: loading ? "..." : totalTasksInView,
      color: OLIVEEB_COLORS.oliveebDarkGreen,
      bg: OLIVEEB_COLORS.greenSoft2,
      to: paths.myTasks,
    },
    {
      label: "Workspaces",
      value: workspacesLoading ? "..." : workspaces.length,
      color: OLIVEEB_COLORS.oliveebBlue,
      bg: OLIVEEB_COLORS.oliveebBlueSoft,
      to: paths.workspaces,
    },
    {
      label: "Pending invitations",
      value: loading ? "..." : pendingInvitations.length,
      color: OLIVEEB_COLORS.oliveebOrange,
      bg: "#FFF3E8",
      to: paths.workspaces,
    },
  ];

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon="home"
          title="Welcome"
          subtitle="A quick, lightweight start point for managing your projects"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        {hasLinkedWorkspace ? (
          <Space wrap>
            <Button
              icon={<AppstoreOutlined />}
              onClick={() => navigate(paths.workspaces)}
              style={{
                borderColor: OLIVEEB_COLORS.oliveebBlue,
                color: OLIVEEB_COLORS.oliveebBlue,
                background: OLIVEEB_COLORS.oliveebBlueSoft,
                fontWeight: 600,
              }}
            >
              Workspaces
            </Button>
            <Button
              type="primary"
              icon={<FolderOpenOutlined />}
              onClick={() => navigate(paths.projects)}
            >
              Projects
            </Button>
          </Space>
        ) : null}
      </div>

      {loadError ? (
        <Alert
          type="warning"
          showIcon
          message="Some welcome data is unavailable"
          description={loadError}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {workspacesLoading ? (
        <Card style={{ borderRadius: 8, borderColor: OLIVEEB_COLORS.border }}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      ) : null}

      {!workspacesLoading && !hasLinkedWorkspace ? (
        <>
          <Card
            style={{
              position: "relative",
              overflow: "hidden",
              borderRadius: 8,
              borderColor: OLIVEEB_COLORS.goldBorder,
              background: `linear-gradient(135deg, #ffffff 0%, ${OLIVEEB_COLORS.goldSoft} 52%, ${OLIVEEB_COLORS.oliveebBlueSoft} 100%)`,
              boxShadow: "0 18px 44px rgba(0, 64, 56, 0.10)",
            }}
          >
            <span style={shineStyle} />
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <span
                style={{
                  display: "inline-grid",
                  placeItems: "center",
                  width: 54,
                  height: 54,
                  borderRadius: 8,
                  background: OLIVEEB_COLORS.oliveebBlueSoft,
                  color: OLIVEEB_COLORS.oliveebBlue,
                  fontSize: 24,
                }}
              >
                <AppstoreOutlined />
              </span>
              <div>
                <Title level={3} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                  Create your first workspace
                </Title>
                <Text style={{ color: OLIVEEB_COLORS.textMuted, display: "block", marginTop: 8, maxWidth: 760 }}>
                  You are not linked to any workspace yet. Create one to unlock projects,
                  testing and validation, subscriptions, charges, and workspace collaboration.
                </Text>
              </div>
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={() => navigate(paths.workspaceCreate())}
                style={{
                  width: "fit-content",
                  borderRadius: 8,
                  fontWeight: 800,
                  background: OLIVEEB_COLORS.oliveebGreen,
                  borderColor: OLIVEEB_COLORS.oliveebGreen,
                }}
              >
                Create workspace
              </Button>
            </Space>
          </Card>

          {loading ? (
            <Card style={{ marginTop: 16, borderRadius: 8, borderColor: OLIVEEB_COLORS.border }}>
              <Skeleton active paragraph={{ rows: 3 }} />
            </Card>
          ) : pendingInvitations.length ? (
            <Card
              title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Pending Invitations</Text>}
              extra={<Tag color="gold">{pendingInvitations.length} pending</Tag>}
              style={{
                marginTop: 16,
                borderRadius: 8,
                borderColor: OLIVEEB_COLORS.goldBorder,
                background: OLIVEEB_COLORS.card,
              }}
            >
              <List
                size="small"
                dataSource={pendingInvitations.slice(0, 4)}
                renderItem={(invitation) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<InboxOutlined style={{ color: OLIVEEB_COLORS.oliveebOrange }} />}
                      title={
                        <Text strong>
                          {invitation.workspaceName ||
                            invitation.workspaceName ||
                            "Workspace invitation"}
                        </Text>
                      }
                      description={
                        <Space wrap size={[4, 4]}>
                          <Tag color="gold">{readable(invitation.membershipStatus, "Pending")}</Tag>
                          {invitation.createdAt ? (
                            <Text type="secondary">{formatDateTime(invitation.createdAt)}</Text>
                          ) : null}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
              <Button
                style={{ marginTop: 12 }}
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(paths.workspaces)}
              >
                Review invitations
              </Button>
            </Card>
          ) : null}
        </>
      ) : null}

      {workspacesLoading || !hasLinkedWorkspace ? null : (
        <>

      <section
        style={glossyPanelStyle}
      >
        <span style={shineStyle} />
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Tag
            color="green"
            style={{
              width: "fit-content",
              marginInlineEnd: 0,
              borderRadius: 999,
              fontWeight: 700,
            }}
          >
            OVACUS_WEB_UI
          </Tag>
          <Title
            level={3}
            style={{
              margin: 0,
              color: OLIVEEB_COLORS.oliveebDarkGreen,
              fontWeight: 900,
            }}
          >
            Welcome back, {userName(user)}
          </Title>
          <Text style={{ color: OLIVEEB_COLORS.textMuted, maxWidth: 900 }}>
            Ovacus helps teams shape product quality with workspaces, project tracking,
            testing and validation flows, subscriptions, and organization payment context in one place.
          </Text>
        </Space>
      </section>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        {stats.map((stat) => (
          <Card
            key={stat.label}
            size="small"
            hoverable
            role="link"
            tabIndex={0}
            onClick={() => navigate(stat.to)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                navigate(stat.to);
              }
            }}
            style={{
              borderRadius: 8,
              borderColor: stat.bg,
              background: `linear-gradient(145deg, #ffffff 0%, ${stat.bg} 100%)`,
              boxShadow: "0 10px 28px rgba(0, 64, 56, 0.08)",
              cursor: "pointer",
            }}
          >
            <Statistic
              title={stat.label}
              value={stat.value}
              valueStyle={{ color: stat.color, fontWeight: 800 }}
              prefix={
                <span
                  style={{
                    display: "inline-grid",
                    placeItems: "center",
                    width: 26,
                    height: 26,
                    borderRadius: 8,
                    background: stat.bg,
                    color: stat.color,
                    marginRight: 4,
                  }}
                >
                  <ThunderboltOutlined />
                </span>
              }
            />
          </Card>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
          gap: 16,
          alignItems: "start",
        }}
      >
        <Card
          title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Current Projects</Text>}
          extra={<Tag color="green">{totalProjects} total</Tag>}
          style={{ borderRadius: 8, borderColor: OLIVEEB_COLORS.border }}
        >
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : projects.length ? (
            <List
              itemLayout="horizontal"
              dataSource={projects}
              renderItem={(project) => (
                <List.Item
                  actions={[
                    <Button
                      key="open"
                      type="link"
                      onClick={() => navigate(paths.projectDetails(project.id))}
                    >
                      Open
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<CheckCircleOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} />}
                    title={
                      <Space wrap size={[6, 4]}>
                        <Text strong>{project.name || "Untitled project"}</Text>
                        {project.code ? <Tag color="blue">{project.code}</Tag> : null}
                        {project.status ? (
                          <Tag color={projectStatusColor(project.status)}>{readable(project.status)}</Tag>
                        ) : null}
                      </Space>
                    }
                    description={
                      <Space wrap size={[8, 4]}>
                        <Text type="secondary">{project.ownerName || "No owner"}</Text>
                        <Text type="secondary">{Number(project.iterationCount || 0)} iterations</Text>
                        <Text type="secondary">{Number(project.taskCount || 0)} tasks</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No current projects found" />
          )}
        </Card>

        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card
            title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Default Workspace</Text>}
            extra={<AppstoreOutlined style={{ color: OLIVEEB_COLORS.oliveebBlue }} />}
            style={workspaceStyles.softCard}
          >
            {workspacesLoading ? (
              <Skeleton active paragraph={{ rows: 2 }} />
            ) : defaultWorkspace ? (
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <div>
                  <Text strong style={{ display: "block", color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                    {workspaceName(defaultWorkspace)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {readable(defaultWorkspace.product, "Product")} /{" "}
                    {readable(defaultWorkspace.accountType, "Workspace type")}
                  </Text>
                </div>
                <Space wrap size={[4, 4]}>
                  {defaultWorkspace.membershipStatus ? (
                    <Tag color="green">{readable(defaultWorkspace.membershipStatus)}</Tag>
                  ) : null}
                  {defaultWorkspace.organizationId ? (
                    <Tag color="cyan">Organization linked</Tag>
                  ) : (
                    <Tag color="gold">No organization</Tag>
                  )}
                </Space>
                <Button
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate(paths.workspaceDetails(defaultWorkspace.id))}
                >
                  Open workspace
                </Button>
              </Space>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No workspace selected" />
            )}
          </Card>

          <Card
            title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Pending Invitations</Text>}
            extra={<Tag color={pendingInvitations.length ? "gold" : "default"}>{pendingInvitations.length}</Tag>}
            style={workspaceStyles.softCard}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : pendingInvitations.length ? (
              <List
                size="small"
                dataSource={pendingInvitations.slice(0, 4)}
                renderItem={(invitation) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<InboxOutlined style={{ color: OLIVEEB_COLORS.oliveebOrange }} />}
                      title={<Text strong>{invitation.workspaceName || "Workspace invitation"}</Text>}
                      description={
                        <Space wrap size={[4, 4]}>
                          <Tag color="gold">{readable(invitation.membershipStatus, "Pending")}</Tag>
                          {invitation.createdAt ? <Text type="secondary">{formatDateTime(invitation.createdAt)}</Text> : null}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No pending invitations" />
            )}
            <Button
              style={{ marginTop: 12 }}
              icon={<ArrowRightOutlined />}
              onClick={() => navigate(paths.workspaces)}
            >
              View workspaces
            </Button>
          </Card>
        </Space>
      </div>
        </>
      )}
    </div>
  );
};

export default WelcomeHomePage;
